import { FormArray, FormGroup } from '@angular/forms';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export class FormValidations {
    public emailPattern() {
        const emailPattern = '^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$';
        return emailPattern;
    }

    public requiredMinCheckbox(min = 1) {
        const validator = (formArray: FormArray) => {
            const totalChecked = formArray.controls
                .map((v) => v.value)
                .reduce((total, current) => (current ? total + current : total), 0);
            return totalChecked >= min ? null : { required: true };
        };
        return validator;
    }

    static getErrorMsg(
        fieldName: string,
        validatorName: string,
        validatorValue?: { requiredLength?: number; max?: number; min?: number },
    ) {
        const config: Record<string, string> = {
            required: `${fieldName} é obrigatório.`,
            minlength: `${fieldName} precisa ter no mínimo ${validatorValue?.requiredLength} caracteres.`,
            maxlength: `${fieldName} precisa ter no máximo ${validatorValue?.requiredLength} caracteres.`,
            max: `${fieldName} precisa ser menor que ${validatorValue?.max}.`,
            min: `${fieldName} precisa ser maior que ${validatorValue?.min}.`,
            cepInvalido: 'CEP inválido.',
            email: 'Email inválido!',
            equalsTo: 'Campos devem ser iguais',
            compareTo: 'A data de término deve ser posterior à data de inicio',
            pattern: 'Campo inválido',
            not_negative: 'O valor deve ser positivo.',
            not_zero_allow: 'O valor deve ser positivo e maior que zero(0).',
            is_bigger: 'O campo de deve ser menor que o campo até.',
        };
        return config[validatorName];
    }
}

/** Translation keys for auth scope (used by login form). */
export const PASSWORD_ERROR_KEYS = {
    required: 'passwordRequired',
    minLength: 'passwordMinLength',
    number: 'passwordNumber',
    uppercase: 'passwordUppercase',
    special: 'passwordSpecial',
} as const;

export const CONFIRM_PASSWORD_ERROR_KEYS = {
    required: 'confirmPasswordRequired',
    minLength: 'confirmPasswordMinLength',
    number: 'confirmPasswordNumber',
    uppercase: 'confirmPasswordUppercase',
    special: 'confirmPasswordSpecial',
} as const;

export function passwordValidator(escopo: string = 'auth.form', confirmPassword = false): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;

        if (!value) {
            return {
                passwordInvalid: `${escopo}.${confirmPassword ? CONFIRM_PASSWORD_ERROR_KEYS.required : PASSWORD_ERROR_KEYS.required}`,
            };
        }

        const hasNumber = /\d/.test(value);
        const hasUpperCase = /[A-Z]/.test(value);
        const hasSpecialChar = /[!@#$%^&*()_+{}\[\]:;<>,.?/~\\|-]/.test(value);
        const hasMinLength = value.length >= 12;

        if (!hasMinLength) {
            return {
                passwordInvalid: `${escopo}.${confirmPassword ? CONFIRM_PASSWORD_ERROR_KEYS.minLength : PASSWORD_ERROR_KEYS.minLength}`,
            };
        }

        if (!hasNumber) {
            return {
                passwordInvalid: `${escopo}.${confirmPassword ? CONFIRM_PASSWORD_ERROR_KEYS.number : PASSWORD_ERROR_KEYS.number}`,
            };
        }

        if (!hasUpperCase) {
            return {
                passwordInvalid: `${escopo}.${confirmPassword ? CONFIRM_PASSWORD_ERROR_KEYS.uppercase : PASSWORD_ERROR_KEYS.uppercase}`,
            };
        }

        if (!hasSpecialChar) {
            return {
                passwordInvalid: `${escopo}.${confirmPassword ? CONFIRM_PASSWORD_ERROR_KEYS.special : PASSWORD_ERROR_KEYS.special}`,
            };
        }

        return null;
    };
}

export function domainValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value: string = control.value;

        // Se o campo estiver vazio, não há erro (pode ser tratado com required se necessário)
        if (!value) {
            return null;
        }

        // Verifica se contém "www." ou "https://"
        if (value.includes('www.') || value.includes('https://')) {
            return { invalidDomain: 'dominios.form.errors.noWwwHttps' };
        }

        // Regex para validar domínios (sem protocolo e sem subdomínios desnecessários)
        const domainRegex =
            /^(?!:\/\/)(?=.{1,253}$)(?:(?!\d+\.)[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

        // Verifica se a string corresponde ao formato de domínio
        if (!domainRegex.test(value)) {
            return { invalidDomain: 'dominios.form.errors.invalid' };
        }

        // Se passou em todas as verificações, o domínio é considerado válido
        return null;
    };
}

export function passwordMatchValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const formGroup = control as FormGroup;
        if (!formGroup) {
            return null;
        }

        const senha = formGroup.get('senha');
        const confirmacaoSenha = formGroup.get('confirmacao_senha');

        if (!senha || !confirmacaoSenha) {
            return null;
        }

        // Se nenhum dos campos foi preenchido, não valida
        if (!senha.value && !confirmacaoSenha.value) {
            return null;
        }

        // Se as senhas não coincidem
        if (senha.value !== confirmacaoSenha.value) {
            confirmacaoSenha.setErrors({ passwordMismatch: true });
            return { passwordMismatch: true };
        } else {
            // Remove o erro de mismatch se existir, mas mantém outros erros
            if (confirmacaoSenha.errors?.['passwordMismatch']) {
                const errors = { ...confirmacaoSenha.errors };
                delete errors['passwordMismatch'];
                confirmacaoSenha.setErrors(Object.keys(errors).length > 0 ? errors : null);
            }
            return null;
        }
    };
}

export function phoneMask(value: string, forceUseDDI?: boolean): string {
    if (!value) return '';

    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');

    if (numbers.length === 0) return '';

    // Detecta DDI (1-3 dígitos começando com 1-9)
    let ddi = '';
    let restOfNumber = numbers;
    let shouldUseDDI = forceUseDDI ?? false;

    // Prioriza números brasileiros (DDI 55)
    if (numbers.startsWith('55') && numbers.length >= 4) {
        ddi = '55';
        restOfNumber = numbers.substring(2);
        shouldUseDDI = true;
    }
    // Tenta detectar DDI de 3 dígitos (212, 213, etc.)
    else if (numbers.length >= 10 && /^[2-9]\d{2}/.test(numbers)) {
        const potentialDDI = numbers.substring(0, 3);
        const potentialRest = numbers.substring(3);
        // Verifica se o restante parece um número válido (pelo menos 7 dígitos)
        if (potentialRest.length >= 7) {
            ddi = potentialDDI;
            restOfNumber = potentialRest;
            shouldUseDDI = true;
        }
    }
    // Tenta detectar DDI de 2 dígitos (44, 33, 49, etc., mas não 55 que já foi tratado)
    else if (numbers.length >= 9 && /^[1-9]\d/.test(numbers) && !numbers.startsWith('55')) {
        const potentialDDI = numbers.substring(0, 2);
        const potentialRest = numbers.substring(2);
        // Verifica se o restante parece um número válido (pelo menos 7 dígitos)
        if (potentialRest.length >= 7) {
            const ddiNum = parseInt(potentialDDI, 10);
            // DDI de 2 dígitos válidos (10-99)
            if (ddiNum >= 10 && ddiNum <= 99) {
                ddi = potentialDDI;
                restOfNumber = potentialRest;
                shouldUseDDI = true;
            }
        }
    }
    // Tenta detectar DDI de 1 dígito (1, 7)
    else if (numbers.length >= 8 && /^[17]/.test(numbers)) {
        const potentialDDI = numbers.substring(0, 1);
        const potentialRest = numbers.substring(1);
        // Verifica se o restante parece um número válido (pelo menos 7 dígitos)
        if (potentialRest.length >= 7) {
            ddi = potentialDDI;
            restOfNumber = potentialRest;
            shouldUseDDI = true;
        }
    }

    // Se forceUseDDI é true, força uso de DDI mesmo se não detectou
    if (forceUseDDI === true && !ddi && numbers.length >= 10) {
        // Tenta detectar DDI brasileiro (55)
        if (numbers.startsWith('55') && numbers.length >= 12) {
            ddi = '55';
            restOfNumber = numbers.substring(2);
            shouldUseDDI = true;
        }
    }

    // Se forceUseDDI é false, não usa DDI mesmo se detectou
    if (forceUseDDI === false && ddi) {
        ddi = '';
        restOfNumber = numbers;
        shouldUseDDI = false;
    }

    // Se não detectou DDI e o número tem 10 ou 11 dígitos, assume formato brasileiro sem DDI
    if (!ddi && !shouldUseDDI && (numbers.length === 10 || numbers.length === 11)) {
        // Formata como (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
        if (numbers.length === 10) {
            return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
        } else {
            return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
        }
    }

    // Se não detectou DDI e tem menos de 10 dígitos, formata progressivamente (sem DDI)
    if (!ddi && !shouldUseDDI) {
        if (numbers.length <= 2) {
            return numbers.length > 0 ? `(${numbers}` : numbers;
        } else if (numbers.length <= 6) {
            return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
        } else if (numbers.length <= 10) {
            return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
        } else {
            return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
        }
    }

    // Formata com DDI
    const ddiFormatted = `+${ddi}`;

    // Para números brasileiros (DDI 55), formata como +55 (XX) XXXXX-XXXX
    if (ddi === '55') {
        if (restOfNumber.length === 0) {
            return ddiFormatted;
        } else if (restOfNumber.length <= 2) {
            return `${ddiFormatted} (${restOfNumber}`;
        } else if (restOfNumber.length <= 6) {
            return `${ddiFormatted} (${restOfNumber.slice(0, 2)}) ${restOfNumber.slice(2)}`;
        } else if (restOfNumber.length <= 10) {
            return `${ddiFormatted} (${restOfNumber.slice(0, 2)}) ${restOfNumber.slice(2, 6)}-${restOfNumber.slice(6)}`;
        } else {
            return `${ddiFormatted} (${restOfNumber.slice(0, 2)}) ${restOfNumber.slice(2, 7)}-${restOfNumber.slice(7, 11)}`;
        }
    }

    // Para outros países, formata como +DDI XXXX XXXX XXXX
    if (restOfNumber.length === 0) {
        return ddiFormatted;
    } else if (restOfNumber.length <= 4) {
        return `${ddiFormatted} ${restOfNumber}`;
    } else if (restOfNumber.length <= 8) {
        return `${ddiFormatted} ${restOfNumber.slice(0, 4)} ${restOfNumber.slice(4)}`;
    } else {
        return `${ddiFormatted} ${restOfNumber.slice(0, 4)} ${restOfNumber.slice(4, 8)} ${restOfNumber.slice(8, 12)}`;
    }
}

export function cpfMask(value: string): string {
    if (!value) return '';

    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');

    // Aplica máscara: XXX.XXX.XXX-XX
    if (numbers.length <= 3) {
        return numbers;
    } else if (numbers.length <= 6) {
        return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    } else if (numbers.length <= 9) {
        return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
    } else {
        return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
    }
}

export function phoneWithDDIValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;

        if (!value) {
            return null; // Deixa o required fazer a validação
        }

        // Remove tudo que não é número
        const numbers = value.replace(/\D/g, '');

        // Verifica se o número tem pelo menos 10 dígitos (mínimo para DDI + DDD + número)
        if (numbers.length < 10) {
            return { invalidPhone: 'Número de telefone incompleto. Inclua DDI e DDD.' };
        }

        // Verifica se começa com um DDI válido (1-3 dígitos)
        // DDI válido: 1-3 dígitos começando com 1-9 (não pode começar com 0)
        const ddiPattern = /^([1-9]\d{0,2})/;
        const ddiMatch = numbers.match(ddiPattern);

        if (!ddiMatch) {
            return {
                invalidPhone: 'DDI inválido. O número deve começar com um código de país válido (ex: 55 para Brasil).',
            };
        }

        const ddi = ddiMatch[1];
        const ddiLength = ddi.length;
        const restOfNumber = numbers.substring(ddiLength);

        // Para números brasileiros (DDI 55), valida formato específico
        if (ddi === '55') {
            // Formato esperado: 55 + DDD (2 dígitos) + número (8 ou 9 dígitos)
            if (restOfNumber.length < 10 || restOfNumber.length > 11) {
                return {
                    invalidPhone:
                        'Número brasileiro inválido. Formato esperado: 55 + DDD (2 dígitos) + número (8 ou 9 dígitos). Exemplo: 5511941566889',
                };
            }

            // Verifica se o DDD é válido (deve estar entre 11 e 99)
            const ddd = restOfNumber.substring(0, 2);
            const dddNum = parseInt(ddd, 10);

            if (dddNum < 11 || dddNum > 99) {
                return { invalidPhone: 'DDD inválido. O DDD deve ter 2 dígitos entre 11 e 99.' };
            }

            // Verifica se o número após o DDD tem 8 ou 9 dígitos
            const phoneNumber = restOfNumber.substring(2);
            if (phoneNumber.length < 8 || phoneNumber.length > 9) {
                return { invalidPhone: 'Número de telefone inválido. Deve ter 8 ou 9 dígitos após o DDD.' };
            }
        } else {
            // Para outros países, validação básica de comprimento
            // DDI (1-3 dígitos) + pelo menos 7 dígitos do número = mínimo 8-10 dígitos
            // Máximo internacional: DDI + 15 dígitos
            const minLength = ddiLength + 7;
            const maxLength = ddiLength + 15;

            if (numbers.length < minLength || numbers.length > maxLength) {
                return {
                    invalidPhone: `Número inválido. Deve ter entre ${minLength} e ${maxLength} dígitos incluindo o DDI.`,
                };
            }
        }

        return null; // Número válido
    };
}
